import type { AudioEngine } from '../audio/synth';

const BEATS = 12;
const INTERVAL = 0.6;
const SKIP = 2;

/**
 * Plays 12 beats and measures how late (or early) the player's taps land.
 * The result shifts the rope drawing and the music so they match the player.
 */
export class Calibrator {
  private beats: number[] = [];
  private taps: number[] = [];
  private running = false;
  private timer = 0;

  constructor(private audio: AudioEngine, private onStatus: (text: string) => void, private onDone: (offsetMs: number | null) => void) {}

  get active(): boolean {
    return this.running;
  }

  start(): void {
    this.audio.unlock();
    if (!this.audio.ready) {
      this.onStatus('No se pudo activar el sonido. Sube el volumen y vuelve a intentarlo.');
      return;
    }
    this.running = true;
    this.taps = [];
    this.beats = [];
    const now = this.audio.now;
    const perfNow = performance.now();
    const latency = this.audio.outputLatency;
    const t0 = now + 0.8;
    for (let i = 0; i < BEATS; i++) {
      const at = t0 + i * INTERVAL;
      this.audio.plopSfx(at);
      this.beats.push(perfNow + (at - now + latency) * 1000);
    }
    this.onStatus('Escucha y toca en cada golpe…');
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.finish(), (0.8 + BEATS * INTERVAL + 0.4) * 1000);
  }

  tap(ts: number): void {
    if (this.running) this.taps.push(ts);
  }

  cancel(): void {
    this.running = false;
    clearTimeout(this.timer);
  }

  private finish(): void {
    this.running = false;
    const diffs: number[] = [];
    for (const tap of this.taps) {
      let best = Infinity;
      let idx = -1;
      this.beats.forEach((b, i) => {
        if (Math.abs(tap - b) < Math.abs(best)) {
          best = tap - b;
          idx = i;
        }
      });
      if (idx >= SKIP && Math.abs(best) < 250) diffs.push(best);
    }
    if (diffs.length < 5) {
      this.onStatus('Hubo pocos toques a tiempo. Inténtalo otra vez tocando en cada golpe.');
      this.onDone(null);
      return;
    }
    diffs.sort((a, b) => a - b);
    const median = diffs[Math.floor(diffs.length / 2)];
    const offset = Math.max(-150, Math.min(200, Math.round(median)));
    this.onDone(offset);
  }
}

export function offsetLabel(ms: number): string {
  if (Math.abs(ms) < 8) return 'Sin ajustar: tus toques llegan a tiempo.';
  return ms > 0 ? `Tus toques llegan ${ms} ms tarde. La cuerda y la música se adelantan para compensar.`
    : `Tus toques llegan ${-ms} ms antes. La cuerda y la música se retrasan para compensar.`;
}
